import type {
  CharacterProfile,
  LockoutGroup,
  PlayerLockoutRow,
  RaidConfig,
  RaidProgress,
  RaidSummary
} from './types';

const MIN_ITEM_LEVEL = 260;

export function buildRaidSummary(characters: CharacterProfile[], raids: RaidConfig[]): RaidSummary {
  const overall = {
    withoutLockout: 0,
    partialLockout: 0,
    fullLockout: 0
  };
  const heroicPlayers = new Map<string, PlayerLockoutRow>();
  const mythicPlayers = new Map<string, PlayerLockoutRow>();
  const diagnostics: string[] = [];

  for (const character of characters) {
    addCharacterDiagnostics(diagnostics, character, raids);

    if (character.itemLevel < MIN_ITEM_LEVEL) {
      continue;
    }

    const heroicGroup = getRaidLockoutGroup(character.heroicLockouts, raids);
    if (heroicGroup) {
      overall[heroicGroup]++;
      addHeroicPlayerLockout(heroicPlayers, character, heroicGroup, raids);
    }

    const mythicGroup = getRaidLockoutGroup(character.mythicLockouts, raids);
    if (mythicGroup) {
      addMythicPlayerLockout(mythicPlayers, character, mythicGroup);
    }
  }

  return {
    overall,
    heroicPlayers: sortPlayerRows([...heroicPlayers.values()]),
    mythicPlayers: sortPlayerRows([...mythicPlayers.values()]).map((row) => ({
      player: row.player,
      withoutLockout: row.withoutLockout,
      withoutLockoutDetails: row.withoutLockoutDetails,
      dominantClass: row.dominantClass
    })),
    diagnostics
  };
}

export function getRaidLockoutGroup(progress: RaidProgress[], raids: RaidConfig[]): LockoutGroup | null {
  if (progress.length !== raids.length) {
    return null;
  }

  if (progress.some((raid) => raid.status || raid.total <= 0)) {
    return null;
  }

  const allWithoutLockout = progress.every((raid) => raid.killed === 0);
  if (allWithoutLockout) {
    return 'withoutLockout';
  }

  const allFullLockout = progress.every((raid) => raid.killed >= raid.total);
  if (allFullLockout) {
    return 'fullLockout';
  }

  return progress.some((raid) => raid.killed > 0) ? 'partialLockout' : null;
}

function addHeroicPlayerLockout(
  rows: Map<string, PlayerLockoutRow>,
  character: CharacterProfile,
  group: LockoutGroup,
  raids: RaidConfig[]
) {
  const row = getPlayerRow(rows, character);

  if (group === 'withoutLockout') {
    row.withoutLockout++;
    row.withoutLockoutDetails.push(formatCharacterDetail(character));
  }

  if (group === 'partialLockout') {
    row.partialLockout++;
    row.partialLockoutDetails.push(`${formatCharacterDetail(character)} - ${formatRaidProgress(character.heroicLockouts, raids)}`);
  }
}

function addMythicPlayerLockout(rows: Map<string, PlayerLockoutRow>, character: CharacterProfile, group: LockoutGroup) {
  const row = getPlayerRow(rows, character);

  if (group === 'withoutLockout') {
    row.withoutLockout++;
    row.withoutLockoutDetails.push(formatCharacterDetail(character));
  }
}

function getPlayerRow(rows: Map<string, PlayerLockoutRow>, character: CharacterProfile): PlayerLockoutRow {
  const key = normalizePlayerKey(character.player);
  const existing = rows.get(key);

  if (existing) {
    return existing;
  }

  const created: PlayerLockoutRow = {
    player: character.player,
    withoutLockout: 0,
    partialLockout: 0,
    withoutLockoutDetails: [],
    partialLockoutDetails: [],
    dominantClass: character.className
  };

  rows.set(key, created);
  return created;
}

function sortPlayerRows<T extends Pick<PlayerLockoutRow, 'player' | 'withoutLockout' | 'partialLockout'>>(rows: T[]): T[] {
  return rows.sort((a, b) => {
    if (b.withoutLockout !== a.withoutLockout) {
      return b.withoutLockout - a.withoutLockout;
    }

    if (b.partialLockout !== a.partialLockout) {
      return b.partialLockout - a.partialLockout;
    }

    return normalizePlayerKey(a.player).localeCompare(normalizePlayerKey(b.player));
  });
}

function addCharacterDiagnostics(diagnostics: string[], character: CharacterProfile, raids: RaidConfig[]) {
  const issues: string[] = [];

  if (!character.player.trim()) {
    issues.push('empty player');
  }

  if (!character.className.trim()) {
    issues.push('empty class');
  }

  if (character.level <= 0) {
    issues.push('level not updated');
  }

  if (character.heroicLockouts.length !== raids.length) {
    issues.push('heroic lockout count mismatch');
  }

  if (character.mythicLockouts.length !== raids.length) {
    issues.push('mythic lockout count mismatch');
  }

  if (issues.length) {
    diagnostics.push(`${character.name}: ${issues.join('; ')}`);
  }
}

function formatCharacterDetail(character: CharacterProfile) {
  return `${character.name} (${character.itemLevel})`;
}

function formatRaidProgress(progress: RaidProgress[], raids: RaidConfig[]) {
  return raids
    .map((raid) => {
      const value = progress.find((entry) => entry.raidId === raid.id);
      return `${raid.shortName} ${value ? `${value.killed}/${value.total}` : '-'}`;
    })
    .join(', ');
}

function normalizePlayerKey(value: string) {
  return value.trim().toLowerCase();
}

